import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../api';

const ProfilePage = () => {
  const { user, updateUser, setRole } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    location: user?.location || '',
  });
  const [providerForm, setProviderForm] = useState({
    business_name: '',
    service_description: '',
    years_experience: '',
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [upgrading, setUpgrading] = useState(false);

  const rwandaLocations = [
    'Kigali', 'Butare (Huye)', 'Gitarama (Muhanga)', 'Ruhengeri (Musanze)',
    'Gisenyi (Rubavu)', 'Byumba (Gicumbi)', 'Cyangugu (Rusizi)',
    'Kibuye (Karongi)', 'Kibungo (Ngoma)', 'Rwamagana',
    'Nyanza', 'Gasabo', 'Kicukiro', 'Nyarugenge', 'Bugesera',
    'Gatsibo', 'Kayonza', 'Kirehe', 'Ngoma', 'Nyagatare',
    'Gakenke', 'Burera', 'Rulindo', 'Nyabihu', 'Ngororero',
    'Kamonyi', 'Ruhango', 'Nyamagabe', 'Nyaruguru', 'Gisagara',
  ];

  useEffect(() => {
    authAPI.getMe().then((res) => {
      if (res.data.providerProfile) {
        const pp = res.data.providerProfile;
        setProviderForm({
          business_name: pp.business_name || '',
          service_description: pp.service_description || '',
          years_experience: pp.years_experience || '',
        });
      }
    }).catch(console.error);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      Object.entries(providerForm).forEach(([k, v]) => formData.append(k, v));
      if (image) formData.append('profile_photo', image);
      const res = await authAPI.updateProfile(formData);
      updateUser(res.data.user);
      setMessage('Profile updated successfully!');
      setImage(null);
      setImagePreview(null);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage('Passwords do not match');
      return;
    }
    setPwLoading(true);
    setMessage('');
    try {
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setMessage('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleUpgradeToProvider = async () => {
    setUpgrading(true);
    setMessage('');
    try {
      const res = await setRole({ role: 'provider' });
      if (res.token) {
        localStorage.setItem('token', res.token);
      }
      setMessage('Account upgraded to Provider! You can now post services and products.');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to upgrade account');
    } finally {
      setUpgrading(false);
    }
  };

  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
  const profileSrc = imagePreview || (user?.profile_photo ? `${apiBase}${user.profile_photo}` : null);

  const tabs = [
    { id: 'personal', label: 'Personal Info' },
    { id: 'business', label: 'Business Profile' },
    { id: 'password', label: 'Security' },
  ];

  return (
    <div className="min-h-screen bg-gray-50/50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-3xl font-bold overflow-hidden flex-shrink-0 ring-4 ring-primary-50">
              {profileSrc ? (
                <img src={profileSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{user?.first_name} {user?.last_name}</h1>
              <p className="text-gray-500">{user?.email}</p>
              {user?.location && <p className="text-gray-400 text-sm mt-1">📍 {user.location}</p>}
            </div>
          </div>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${message.includes('success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {message}
          </div>
        )}

        {/* Upgrade to Provider Banner */}
        {user?.role === 'customer' && (
          <div className="mb-6 bg-gradient-to-r from-primary-50 to-green-50 rounded-2xl shadow-sm border border-primary-100 p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.15c0 .414.336.75.75.75z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Want to sell or offer services?</h3>
                <p className="text-sm text-gray-600 mt-0.5">Upgrade to a Provider account to post services and products on TopInfo.</p>
              </div>
              <button
                onClick={handleUpgradeToProvider}
                disabled={upgrading}
                className="px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 transition shadow-md shadow-primary-500/20 whitespace-nowrap"
              >
                {upgrading ? 'Upgrading...' : 'Become a Provider'}
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-2xl shadow-sm border border-gray-100 p-1.5">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === tab.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Personal Info Tab */}
        {activeTab === 'personal' && (
          <form onSubmit={handleUpdate} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold mb-4">Personal Information</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Profile Photo</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-2xl font-bold overflow-hidden">
                  {profileSrc ? (
                    <img src={profileSrc} alt="" className="w-full h-full object-cover" />
                  ) : (
                    `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`
                  )}
                </div>
                <div>
                  <input type="file" accept="image/*" onChange={handleImageChange}
                    className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG or WebP. Max 5MB.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name</label>
                <input type="text" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Name</label>
                <input type="text" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input type="tel" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="+250 7XX XXX XXX" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Select location</option>
                  {rwandaLocations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="mt-6 bg-primary-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        )}

        {/* Business Profile Tab */}
        {activeTab === 'business' && (
          <form onSubmit={handleUpdate} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold mb-2">Business Profile</h2>
            <p className="text-gray-500 text-sm mb-6">This information appears on your public profile when you list services or products.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Business / Display Name</label>
                <input type="text" value={providerForm.business_name}
                  onChange={(e) => setProviderForm({ ...providerForm, business_name: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., John's Tech Solutions" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">About Your Services</label>
                <textarea rows={4} value={providerForm.service_description}
                  onChange={(e) => setProviderForm({ ...providerForm, service_description: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Describe what services or products you offer, your expertise, etc." />
              </div>
              <div className="max-w-xs">
                <label className="block text-sm font-medium mb-1">Years of Experience</label>
                <input type="number" min="0" max="50" value={providerForm.years_experience}
                  onChange={(e) => setProviderForm({ ...providerForm, years_experience: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0" />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="mt-6 bg-primary-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50">
              {loading ? 'Saving...' : 'Save Business Profile'}
            </button>
          </form>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordChange} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold mb-4">Change Password</h2>
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium mb-1">Current Password</label>
                <input type="password" value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New Password</label>
                <input type="password" value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500"
                  minLength={6} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Confirm New Password</label>
                <input type="password" value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500"
                  minLength={6} required />
              </div>
            </div>
            <button type="submit" disabled={pwLoading}
              className="mt-6 bg-gray-800 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-gray-900 disabled:opacity-50">
              {pwLoading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
